import { useState, useEffect } from 'react';
import { Loader2, Cloud, Sun, CloudRain, CloudSnow, Wind, Thermometer, Droplets, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';

// Weather condition icons
const getWeatherIcon = (condition) => {
  const icons = {
    sunny: Sun,
    cloudy: Cloud,
    rainy: CloudRain,
    snowy: CloudSnow,
    windy: Wind,
  };
  return icons[condition] || Cloud;
};

// Mock data for simulation
const MOCK_WEATHER = {
  location: 'London, UK',
  temperature: 12,
  condition: 'cloudy',
  description: 'Partly cloudy',
  humidity: 75,
  wind_speed: 15,
  feels_like: 10,
  visibility: 10,
  forecast: [
    { day: 'Tue', temp: 14, condition: 'sunny' },
    { day: 'Wed', temp: 11, condition: 'rainy' },
    { day: 'Thu', temp: 13, condition: 'cloudy' },
    { day: 'Fri', temp: 15, condition: 'sunny' },
  ]
};

export function WeatherWidgetTile({ filter, size, useMockData }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (useMockData) {
      setWeather(MOCK_WEATHER);
      setLoading(false);
      return;
    }

    // For real implementation, would use a weather API
    // For now, show mock data with location from browser
    const loadWeather = async () => {
      try {
        setLoading(true);

        // Get user's location
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              // In production, call weather API with lat/long
              // For now, use mock data
              setWeather({
                ...MOCK_WEATHER,
                location: 'Your Location'
              });
              setLoading(false);
            },
            () => {
              // Location denied, use default
              setWeather(MOCK_WEATHER);
              setLoading(false);
            }
          );
        } else {
          setWeather(MOCK_WEATHER);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load weather:', err);
        setError('Unable to load weather');
        setLoading(false);
      }
    };

    loadWeather();
  }, [useMockData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-sky-600" />
      </div>
    );
  }

  if (error || !weather) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        <div className="bg-gray-100 p-3 rounded-full mb-3">
          <Cloud className="h-6 w-6 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-900">Weather unavailable</p>
        <p className="text-xs text-gray-500 mt-1">{error || 'Try again later'}</p>
      </div>
    );
  }

  const WeatherIcon = getWeatherIcon(weather.condition);
  const isCompact = size === 'small';

  return (
    <div className="h-full flex flex-col">
      {/* Main weather display */}
      <div className="flex items-start gap-3 mb-3">
        <div className={cn(
          "rounded-xl flex items-center justify-center",
          weather.condition === 'sunny' ? 'bg-amber-100' :
          weather.condition === 'rainy' ? 'bg-blue-100' :
          weather.condition === 'snowy' ? 'bg-slate-100' :
          'bg-gray-100',
          isCompact ? 'p-2' : 'p-3'
        )}>
          <WeatherIcon className={cn(
            weather.condition === 'sunny' ? 'text-amber-500' :
            weather.condition === 'rainy' ? 'text-blue-500' :
            weather.condition === 'snowy' ? 'text-slate-500' :
            'text-gray-500',
            isCompact ? 'h-6 w-6' : 'h-8 w-8'
          )} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-1">
            <span className={cn("font-bold text-gray-900", isCompact ? 'text-2xl' : 'text-3xl')}>
              {weather.temperature}°
            </span>
            <span className="text-gray-500 text-sm">C</span>
          </div>
          <p className="text-xs text-gray-600 truncate">{weather.description}</p>
          <p className="text-[10px] text-gray-400 truncate">{weather.location}</p>
        </div>
      </div>

      {/* Weather details */}
      {!isCompact && (
        <div className="grid grid-cols-3 gap-2 mb-3 py-2 border-y">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-gray-500 mb-0.5">
              <Thermometer className="h-3 w-3" />
            </div>
            <p className="text-xs font-medium text-gray-900">{weather.feels_like}°</p>
            <p className="text-[10px] text-gray-500">Feels like</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-gray-500 mb-0.5">
              <Droplets className="h-3 w-3" />
            </div>
            <p className="text-xs font-medium text-gray-900">{weather.humidity}%</p>
            <p className="text-[10px] text-gray-500">Humidity</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-gray-500 mb-0.5">
              <Wind className="h-3 w-3" />
            </div>
            <p className="text-xs font-medium text-gray-900">{weather.wind_speed}</p>
            <p className="text-[10px] text-gray-500">km/h</p>
          </div>
        </div>
      )}

      {/* Forecast */}
      {!isCompact && weather.forecast && (
        <div className="flex-1 flex items-end">
          <div className="w-full grid grid-cols-4 gap-1">
            {weather.forecast.map((day, i) => {
              const DayIcon = getWeatherIcon(day.condition);
              return (
                <div key={i} className="text-center p-1.5 rounded-lg bg-gray-50">
                  <p className="text-[10px] text-gray-500 mb-1">{day.day}</p>
                  <DayIcon className={cn(
                    "h-4 w-4 mx-auto mb-1",
                    day.condition === 'sunny' ? 'text-amber-500' :
                    day.condition === 'rainy' ? 'text-blue-500' :
                    'text-gray-400'
                  )} />
                  <p className="text-xs font-medium text-gray-900">{day.temp}°</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
